import { Space, Table, Button, Col, Row, Statistic, Modal, Descriptions, Tabs, DatePicker, Typography } from 'antd';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { formatDate } from 'utils/date';
import { formatCurrency } from 'utils/number';
import Debt from '../Debt';
import CustomerInfoEdit from 'components/customer/CustomerInfoEdit';
import CustomerLoginEdit from 'components/customer/CustomerLoginEdit';
import ManualAdjustModal from './ManualAdjustModal';
import _ from 'lodash';

const DesktopInfoModal = ({
  isModalOpen,
  customer,
  orders,
  loyaltyEnabled,
  loyaltyByOrder,
  manualAdjustments,
  reload,
  showCustomerEdit,
  setShowCustomerEdit,
  showCustomerLoginEdit,
  setShowCustomerLoginEdit,
  isAccountLoginMode,
  handleCancel,
  onCustomerSave,
}) => {
  const navigate = useNavigate();
  const [pointFromDate, setPointFromDate] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  const columns = [
    {
      title: 'Mã đơn hàng',
      render: (record) => (
        <Space direction="vertical">
          <Button type="text" onClick={() => navigate(`/order/${record.id}`)}>
            {record.id}
          </Button>
          <div>{formatDate(record.create_on)}</div>
        </Space>
      ),
    },
    {
      title: 'Sản phẩm',
      render: (record) => (
        <Space direction="vertical">
          <div>{record.total_item}</div>
          <div>{formatCurrency(record.total_amount)}</div>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      render: (record) => (
        <Space direction="vertical">
          {record.status === 'canceled' && <div>Đã Huỷ</div>}
          {record.status === 'finalized' && <div>Đã Hoàn tất</div>}
          {!['canceled', 'finalized'].includes(record.status) && <div>Đang xử lý</div>}
        </Space>
      ),
    },
  ];

  const items = [
    {
      key: 'order',
      label: 'Đơn hàng',
      children: <Table dataSource={orders} columns={columns} scroll={{ y: 300 }} />,
    },
    {
      key: 'debt',
      label: 'Công nợ',
      children: <Debt customer={customer} onChange={onCustomerSave} />,
    },
  ];

  const pointHistoryRows = useMemo(() => {
    const orderRows = (orders || []).map((o) => {
      const lp = loyaltyByOrder?.[o.id] || { earn: 0, use: 0 };
      return {
        rowKey: `order_${o.id}`,
        kind: 'order',
        id: o.id,
        create_on: o.create_on,
        sub_amount: o.sub_amount,
        total_amount: o.total_amount,
        earn: lp.earn || 0,
        use: lp.use || 0,
      };
    });
    const manualRows = (manualAdjustments || []).map((m) => ({
      rowKey: `manual_${m.id}`,
      kind: 'manual',
      id: m.id,
      create_on: m.create_on,
      reason: m.reason,
      earn: m.earn || 0,
      use: m.use || 0,
    }));
    let list = _.orderBy([...orderRows, ...manualRows], ['create_on'], ['desc']);
    if (!pointFromDate) return list;
    const fromTs = pointFromDate.startOf('day').valueOf();
    return list.filter((o) => o.create_on && new Date(o.create_on).getTime() >= fromTs);
  }, [orders, loyaltyByOrder, manualAdjustments, pointFromDate]);

  const pointColumns = [
    {
      title: 'Mã đơn hàng',
      render: (record) => {
        if (record.kind === 'manual') {
          return (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>Điều chỉnh tay</Typography.Text>
              <div>{formatDate(record.create_on)}</div>
              {record.reason && (
                <Typography.Text type="secondary" italic>
                  {record.reason}
                </Typography.Text>
              )}
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={0}>
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/order/${record.id}`)}>
              {record.id}
            </Button>
            <div>{formatDate(record.create_on)}</div>
            <div>{formatCurrency(record.sub_amount ?? record.total_amount ?? 0)}</div>
          </Space>
        );
      },
    },
    {
      title: 'Xu tích được',
      align: 'right',
      render: (record) => formatCurrency(record.earn || 0),
    },
    {
      title: 'Xu sử dụng',
      align: 'right',
      render: (record) => formatCurrency(record.use || 0),
    },
  ];

  if (loyaltyEnabled) {
    items.push({
      key: 'loyalty',
      label: 'Lịch sử tích điểm',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <span>Từ ngày:</span>
              <DatePicker
                value={pointFromDate}
                onChange={(d) => setPointFromDate(d)}
                format="DD/MM/YYYY"
                allowClear
              />
            </Space>
            <Button type="primary" onClick={() => setShowAdjustModal(true)}>
              Điều chỉnh xu
            </Button>
          </Space>
          <Table
            rowKey="rowKey"
            dataSource={pointHistoryRows}
            columns={pointColumns}
            scroll={{ y: 300 }}
            pagination={false}
            summary={(rows) => {
              const totalEarn = _.sumBy(rows, (r) => r.earn || 0);
              const totalUse = _.sumBy(rows, (r) => r.use || 0);
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <strong>Tổng</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(totalEarn)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{formatCurrency(totalUse)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Space>
      ),
    });
  }

  return (
    <>
      <Modal
        title="Thông tin khách hàng"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalOpen}
        onCancel={handleCancel}
        width="50%"
        footer={
          <Space>
            <Button type="link" onClick={() => setShowCustomerEdit(true)}>
              Cập nhật thông tin
            </Button>
            {isAccountLoginMode && (
              <Button type="link" onClick={() => setShowCustomerLoginEdit(true)}>
                Quản lý đăng nhập 
              </Button>
            )}
            <Button onClick={handleCancel}>Close</Button>
          </Space>
        }
      >
        <Space style={{ width: '100%' }} direction="vertical">
          <Descriptions column={1}>
            <Descriptions.Item label="Họ và tên">{customer.full_name}</Descriptions.Item>
            <Descriptions.Item label="SDT">{customer.phone}</Descriptions.Item>
            <Descriptions.Item label="Địa chỉ" span={3}>
              {customer.address}
            </Descriptions.Item>
          </Descriptions>

          <Row gutter={16}>
            <Col flex="25%">
              <Statistic title="Đơn hàng" value={orders.length} />
            </Col>
            <Col flex="25%">
              <Statistic
                title="Số tiền"
                value={_.sumBy(orders, (x) => x.total_amount ?? 0)}
                precision={0}
              />
            </Col>
            <Col flex="25%">
              <Statistic title="Xu" value={customer.loyalty_point?.current ?? 0} precision={0} />
            </Col>
            {customer.debt?.current_debt !== 0 && (
              <Col flex="25%">
                <Statistic title="Nợ" value={customer.debt?.current_debt ?? 0} precision={0} />
              </Col>
            )}
          </Row>
          <Tabs items={items} />
        </Space>
      </Modal>

      <CustomerInfoEdit
        id={customer.id}
        open={showCustomerEdit}
        onSave={onCustomerSave}
        onClose={() => setShowCustomerEdit(false)}
      />
      <CustomerLoginEdit
        id={customer.id}
        open={showCustomerLoginEdit}
        onSave={onCustomerSave}
        onClose={() => setShowCustomerLoginEdit(false)}
      />
      <ManualAdjustModal
        open={showAdjustModal}
        customer={customer}
        onClose={() => setShowAdjustModal(false)}
        onAdjusted={reload}
      />
    </>
  );
};

export default DesktopInfoModal;
